import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import { kanbanActions } from "../../../redux/kanban/kanban.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Rounded } from "../../../theme/Rounded";
import { AtiraToast } from "../../../utils/AtiraToast";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { useDrawersContext } from "../../tasks/drawers-context";
import { Clickable } from "../../../components/Clickable";
import { Shadow } from "../../../theme/Shadow";

const AddKanbanWrapper = styled(Flex)`
  flex-direction: column;
  justify-content: center;
  align-items: center;

  min-width: 18rem;
  height: 98%;

  background-color: ${({ theme }) => theme.white};
  border: 2px dashed ${({ theme }) => theme.darkSub};
  border-radius: ${Rounded.lg};
  box-shadow: ${Shadow.SM};

  color: ${({ theme }) => theme.main};
  cursor: pointer;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    color 0.2s;

  * {
    color: inherit;
  }

  &:hover {
    background-color: ${({ theme }) => theme.sub};
    border-color: ${({ theme }) => theme.main};
  }
`;

type KanbanColumnAddProps = {
  canAddColumn: boolean;
};

export const KanbanColumnAdd: React.FC<KanbanColumnAddProps> = ({
  canAddColumn,
}) => {
  const [addColumnLoading, setAddColumnLoading] = useState(false);

  const { setSubToUnlock } = useDrawersContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const kanban = useAppSelector(kanbanSliceSelectors.selectUserCurrentKanban);

  const addColumn = async () => {
    try {
      if (!canAddColumn) {
        setSubToUnlock({
          visible: true,
          title: t("subToUnlock.specific.kanbanColumnsCount", { limit: 9 }),
        });
        return;
      }

      setAddColumnLoading(true);
      await dispatch(
        kanbanActions.createKanbanColumn({
          userId: user._id,
          kanbanId: kanban?._id!,
        }),
      ).unwrap();
      await dispatch(
        kanbanActions.getUserKanban({
          userId: user._id,
          kanbanId: kanban?._id,
        }),
      ).unwrap();
      AtiraToast.success(t("deals.column.add.success"));
    } catch (e) {
      console.error(e);
      AtiraToast.apiError(e);
    } finally {
      setAddColumnLoading(false);
    }
  };

  return (
    <AddKanbanWrapper>
      <Clickable onClick={addColumn}>
        {!addColumnLoading ? (
          <Flex alignItems="center" gap="s">
            <Text>{t("common.add_column")}</Text>

            <AtiraIcon icon={faPlus} size="2x" />
          </Flex>
        ) : (
          <SpinnerFullScreen />
        )}
      </Clickable>
    </AddKanbanWrapper>
  );
};
